// iter687 — Country preset registry. Source of truth for region-
// specific defaults: VAT rate + label, currency, compliance flags
// (ZATCA, FTA-UAE, Zakat applicability), language, date format,
// weekend days. Used by the Setup Wizard, branch creator, and any
// settings UI that wants to hide/show country-specific options.
//
// Add a new country here and it shows up everywhere — wizard,
// branch picker, conditional toggles. No code changes elsewhere
// required.
//
// Each entry intentionally minimal: only the fields that actually
// drive UI / business logic. Lists like national holidays live
// separately so this stays tight.

export const COUNTRIES = [
  {
    code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', region: 'GCC',
    currency_code: 'SAR', currency_symbol: '﷼',
    default_vat_rate: 15, vat_label: 'VAT',
    has_zakat: true, has_zatca_phase1: true, has_zatca_phase2: true,
    decimal_places: 2, date_format: 'YYYY-MM-DD',
    weekend_days: [5, 6], // Fri, Sat
    language_hint: 'ar',
    receipt_header_default: 'فاتورة ضريبية مبسطة\nSimplified Tax Invoice',
  },
  {
    code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', region: 'GCC',
    currency_code: 'AED', currency_symbol: 'AED',
    default_vat_rate: 5, vat_label: 'VAT',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    has_fta_uae: true,
    decimal_places: 2, date_format: 'DD/MM/YYYY',
    weekend_days: [5, 6],
    language_hint: 'ar',
    receipt_header_default: 'Tax Invoice / فاتورة ضريبية',
  },
  {
    code: 'BH', name: 'Bahrain', flag: '🇧🇭', region: 'GCC',
    currency_code: 'BHD', currency_symbol: 'BD',
    default_vat_rate: 10, vat_label: 'VAT',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 3, date_format: 'DD/MM/YYYY',
    weekend_days: [5, 6], language_hint: 'ar',
    receipt_header_default: 'Tax Invoice / فاتورة ضريبية',
  },
  {
    code: 'KW', name: 'Kuwait', flag: '🇰🇼', region: 'GCC',
    currency_code: 'KWD', currency_symbol: 'KD',
    default_vat_rate: 0, vat_label: 'VAT',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 3, date_format: 'DD/MM/YYYY',
    weekend_days: [5, 6], language_hint: 'ar',
    receipt_header_default: 'فاتورة',
  },
  {
    code: 'QA', name: 'Qatar', flag: '🇶🇦', region: 'GCC',
    currency_code: 'QAR', currency_symbol: 'QR',
    default_vat_rate: 0, vat_label: 'VAT',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 2, date_format: 'DD/MM/YYYY',
    weekend_days: [5, 6], language_hint: 'ar',
    receipt_header_default: 'فاتورة',
  },
  {
    code: 'OM', name: 'Oman', flag: '🇴🇲', region: 'GCC',
    currency_code: 'OMR', currency_symbol: 'R.O',
    default_vat_rate: 5, vat_label: 'VAT',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 3, date_format: 'DD/MM/YYYY',
    weekend_days: [5, 6], language_hint: 'ar',
    receipt_header_default: 'Tax Invoice / فاتورة ضريبية',
  },
  {
    code: 'EG', name: 'Egypt', flag: '🇪🇬', region: 'MENA',
    currency_code: 'EGP', currency_symbol: 'E£',
    default_vat_rate: 14, vat_label: 'VAT',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 2, date_format: 'DD/MM/YYYY',
    weekend_days: [5, 6], language_hint: 'ar',
    receipt_header_default: 'Tax Invoice / فاتورة ضريبية',
  },
  {
    code: 'IN', name: 'India', flag: '🇮🇳', region: 'South Asia',
    currency_code: 'INR', currency_symbol: '₹',
    default_vat_rate: 18, vat_label: 'GST',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 2, date_format: 'DD/MM/YYYY',
    weekend_days: [0], language_hint: 'en',
    receipt_header_default: 'Tax Invoice',
  },
  {
    code: 'PK', name: 'Pakistan', flag: '🇵🇰', region: 'South Asia',
    currency_code: 'PKR', currency_symbol: 'Rs',
    default_vat_rate: 17, vat_label: 'GST',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 2, date_format: 'DD/MM/YYYY',
    weekend_days: [0], language_hint: 'en',
    receipt_header_default: 'Tax Invoice',
  },
  {
    code: 'US', name: 'United States', flag: '🇺🇸', region: 'North America',
    currency_code: 'USD', currency_symbol: '$',
    default_vat_rate: 0, vat_label: 'Sales Tax',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 2, date_format: 'MM/DD/YYYY',
    weekend_days: [0, 6], language_hint: 'en',
    receipt_header_default: 'Sales Receipt',
  },
  {
    code: 'GB', name: 'United Kingdom', flag: '🇬🇧', region: 'Europe',
    currency_code: 'GBP', currency_symbol: '£',
    default_vat_rate: 20, vat_label: 'VAT',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 2, date_format: 'DD/MM/YYYY',
    weekend_days: [0, 6], language_hint: 'en',
    receipt_header_default: 'VAT Receipt',
  },
  {
    code: 'OTHER', name: 'Other / Not listed', flag: '🌍', region: 'Other',
    currency_code: 'USD', currency_symbol: '$',
    default_vat_rate: 0, vat_label: 'Tax',
    has_zakat: false, has_zatca_phase1: false, has_zatca_phase2: false,
    decimal_places: 2, date_format: 'YYYY-MM-DD',
    weekend_days: [0, 6], language_hint: 'en',
    receipt_header_default: 'Receipt',
  },
];

/**
 * Look up a country by ISO code. Returns null if not found so the
 * caller can decide whether to fall back to a default or surface an
 * error (e.g. existing tenant whose country_code was set before this
 * registry shipped).
 */
export function getCountry(code) {
  if (!code) return null;
  return COUNTRIES.find(c => c.code === String(code).toUpperCase()) || null;
}

/**
 * Returns the country code → derived flag dict that the conditional
 * UI uses to hide/show region-specific toggles. Example:
 *   countryFlags('SA') → { has_zakat: true, has_zatca_phase1: true, … }
 * Existing tenants without a country are treated as "show everything"
 * so the UX matches today's behavior until they pick a country.
 */
export function countryFlags(code) {
  const c = getCountry(code);
  if (!c) return {
    has_zakat: true, has_zatca_phase1: true, has_zatca_phase2: true,
    has_fta_uae: true,
  };
  return {
    has_zakat: !!c.has_zakat,
    has_zatca_phase1: !!c.has_zatca_phase1,
    has_zatca_phase2: !!c.has_zatca_phase2,
    has_fta_uae: !!c.has_fta_uae,
  };
}
